/**
 * AccountPage.jsx — 用户账户与支付管理
 * Shows tier, credits, payment history, subscriptions, credit log.
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../constants/config";

const GD   = "#D4AF37";
const GDD  = "#B8940A";
const BG   = "#FDFBF7";
const TXT  = "#1A202C";
const TXT2 = "#4A5568";
const TXT3 = "#718096";

export default function AccountPage({ lang = "zh", user, authLoading, onLogin }) {
  const nav = useNavigate();
  const zh = lang === "zh";
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState("");
  const [creditLog, setCreditLog] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [refundingId, setRefundingId] = useState(null);
  const [refundToast, setRefundToast] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.token) { nav("/login?redirect=/account"); return; }
    setError("");
    const headers = { Authorization: "Bearer " + user.token };
    // Fetch account + credit log + payments in parallel
    Promise.all([
      fetch(`${API}/auth/me`, { headers }).then(r => r.ok ? r.json() : Promise.reject("HTTP " + r.status)),
      fetch(`${API}/account/credit-log`, { headers }).then(r => r.ok ? r.json() : []),
      fetch(`${API}/account/payments`, { headers }).then(r => r.ok ? r.json() : { payments: [] }),
    ])
      .then(([acc, log, pay]) => {
        setAccount(acc);
        setCreditLog(log || []);
        setPayments(pay?.payments || []);
        setPaymentsLoading(false);
      })
      .catch(e => setError(zh ? "加载账户信息失败" : "Failed to load account"))
      .finally(() => setLoading(false));
  }, [user, authLoading]);

  const handlePortal = async () => {
    setPortalLoading(true);
    setPortalError("");
    try {
      const res = await fetch(`${API}/payments/portal`, {
        method: "GET",
        headers: { Authorization: "Bearer " + user.token },
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      const data = await res.json();
      if (data.portal_url) window.location.href = data.portal_url;
      else throw new Error("No portal URL");
    } catch (e) {
      setPortalError(zh ? "无法打开账单管理，请稍后重试" : "Unable to open billing. Please try again.");
    }
    setPortalLoading(false);
  };

  const handleRefund = async (paymentId, paymentIntent) => {
    if (!paymentIntent) return;
    const msg = zh ? `确认退款此笔支付？退款后积分/权益将被收回。` : `Confirm refund for this payment? Credits will be revoked.`;
    if (!window.confirm(msg)) return;
    setRefundingId(paymentId);
    try {
      const res = await fetch(`${API}/account/refund`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + user.token },
        body: JSON.stringify({ payment_intent_id: paymentIntent }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "HTTP " + res.status);
      }
      const data = await res.json();
      if (data.status === "succeeded") {
        // Refresh payment list
        const payRes = await fetch(`${API}/account/payments`, { headers: { Authorization: "Bearer " + user.token } });
        if (payRes.ok) {
          const payData = await payRes.json();
          setPayments(payData?.payments || []);
        }
        setRefundToast({type: "success", msg: zh ? "退款已处理" : "Refund processed"});
        setTimeout(() => setRefundToast(null), 3000);
      }
    } catch (e) {
      setRefundToast({type: "error", msg: (zh ? "退款失败: " : "Refund failed: ") + e.message});
      setTimeout(() => setRefundToast(null), 4000);
    }
    setRefundingId(null);
  };

  if (authLoading || loading) {
    return <div style={{ minHeight: "100vh", background: BG, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ fontSize: 14, color: TXT3 }}>{zh ? "加载中…" : "Loading…"}</div>
    </div>;
  }

  if (!account) {
    return <div style={{ minHeight: "100vh", background: BG, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
      <div style={{ fontSize: 14, color: TXT3 }}>{error || (zh ? "请先登录" : "Please sign in")}</div>
      <button onClick={() => nav("/login?redirect=/account")} style={{ padding: "8px 24px", borderRadius: 8, border: `1px solid ${GD}`, background: "transparent", color: GD, cursor: "pointer" }}>{zh ? "登录" : "Sign In"}</button>
    </div>;
  }

  const tierLabel = { free: zh ? "免费" : "Free", pro: "Pro" }[account.tier] || account.tier;
  const tierColor = account.tier === "pro" ? GD : TXT3;

  // Format date from Stripe timestamp
  const fmtDate = (ts) => {
    if (!ts) return "";
    const d = new Date(ts * 1000);
    return zh ? d.toLocaleDateString("zh-CN") : d.toLocaleDateString();
  };

  // Format Stripe amount
  const fmtAmount = (cents, currency) => {
    if (!cents) return "";
    const amt = cents / 100;
    return currency === "cad" ? `$${amt.toFixed(2)} CAD` : `$${amt.toFixed(2)} USD`;
  };

  return (
    <div style={{ minHeight: "100vh", background: BG, fontFamily: "\"Inter\",\"Noto Serif SC\",serif", color: TXT, paddingBottom: 80 }}>
      {/* Nav */}
      <div style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(253,251,247,.95)", backdropFilter: "blur(12px)", borderBottom: "1px solid rgba(139,105,20,.1)", padding: "10px 24px", display: "flex", alignItems: "center", gap: 24 }}>
        <span onClick={() => nav("/")} style={{ fontSize: 15, fontWeight: 600, color: GD, cursor: "pointer", letterSpacing: 1 }}>
          ☯ {zh ? "澄光命盘" : "LUMINA"}
        </span>
        <div style={{ flex: 1 }} />
        <button onClick={() => nav("/pricing")} style={{ padding: "6px 16px", borderRadius: 8, border: `1px solid ${GD}`, background: "transparent", color: GD, cursor: "pointer", fontSize: 12 }}>
          {zh ? "升级方案" : "Plans"}
        </button>
      </div>

      {/* Header */}
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "48px 24px 0" }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>
          {zh ? "我的账户" : "My Account"}
        </h1>
        <p style={{ fontSize: 14, color: TXT3 }}>{account.email}</p>
      </div>

      {/* Error banner */}
      {error && (
        <div style={{ maxWidth: 720, margin: "12px auto 0", padding: "0 24px" }}>
          <div style={{ padding: "10px 16px", borderRadius: 10, background: "rgba(185,28,28,0.06)", border: "1px solid rgba(185,28,28,0.12)", color: "#b91c1c", fontSize: 13, textAlign: "center" }}>{error}</div>
        </div>
      )}

      {/* Status Cards */}
      <div style={{ maxWidth: 720, margin: "24px auto 0", padding: "0 24px", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
        {/* Tier */}
        <div style={{ background: "#fff", borderRadius: 14, padding: "24px 20px", border: "1px solid rgba(0,0,0,0.05)", textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.02)" }}>
          <div style={{ fontSize: 11, color: TXT3, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>{zh ? "当前方案" : "Plan"}</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: tierColor }}>{tierLabel}</div>
          {account.pro_expires_at && (
            <div style={{ fontSize: 11, color: TXT3, marginTop: 4 }}>
              {zh ? `到期 ${new Date(account.pro_expires_at).toLocaleDateString("zh-CN")}` : `Expires ${new Date(account.pro_expires_at).toLocaleDateString()}`}
            </div>
          )}
        </div>

        {/* Oracle Credits */}
        <div style={{ background: "#fff", borderRadius: 14, padding: "24px 20px", border: "1px solid rgba(0,0,0,0.05)", textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.02)" }}>
          <div style={{ fontSize: 11, color: TXT3, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>{zh ? "问事次数" : "Oracle Credits"}</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: TXT }}>{account.oracle_credits ?? 0}</div>
        </div>

        {/* Stripe Portal */}
        <div style={{ background: "#fff", borderRadius: 14, padding: "24px 20px", border: "1px solid rgba(0,0,0,0.05)", textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.02)", display: "flex", flexDirection: "column", justifyContent: "center", gap: 8 }}>
          <div style={{ fontSize: 11, color: TXT3, letterSpacing: 2, textTransform: "uppercase" }}>{zh ? "账单管理" : "Billing"}</div>
          <button
            onClick={handlePortal}
            disabled={portalLoading}
            style={{
              padding: "8px 20px", borderRadius: 8, border: `1px solid ${GD}`, background: "transparent",
              color: GD, cursor: portalLoading ? "wait" : "pointer", fontSize: 13, fontWeight: 500,
            }}
          >
            {portalLoading ? (zh ? "加载中…" : "Loading…") : (zh ? "管理账单 →" : "Manage →")}
          </button>
          {portalError && <div style={{ fontSize: 11, color: "#b91c1c", marginTop: 4 }}>{portalError}</div>}
        </div>
      </div>

      {/* Purchases — Quick Actions */}
      <div style={{ maxWidth: 720, margin: "32px auto 0", padding: "0 24px" }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: TXT, marginBottom: 12 }}>
          {zh ? "服务与购买" : "Services"}
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
          {[
            { label: zh ? "购买问事" : "Oracle Q", price: "$9.99", nav: "/pricing", hl: false },
            { label: zh ? "命盘报告" : "Life Chart", price: "$39.99", nav: "/pricing", hl: false },
            { label: zh ? "升级 Pro" : "Go Pro", price: "$49.99/mo", nav: "/pricing", hl: true },
            { label: zh ? "合盘分析" : "Synastry", price: "$79.99", nav: "/pricing", hl: false },
          ].map((item, i) => (
            <div key={i} onClick={() => nav(item.nav)} style={{
              background: item.hl ? `linear-gradient(135deg, ${GD}, ${GDD})` : "#fff",
              borderRadius: 12, padding: "16px 14px", cursor: "pointer",
              border: item.hl ? "none" : "1px solid rgba(0,0,0,0.06)",
              color: item.hl ? "#fff" : TXT,
              boxShadow: item.hl ? "0 4px 16px rgba(212,175,55,0.15)" : "0 1px 4px rgba(0,0,0,0.03)",
              transition: "transform 0.15s",
            }}
            onMouseOver={e => e.currentTarget.style.transform = "translateY(-2px)"}
            onMouseOut={e => e.currentTarget.style.transform = "none"}
            >
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{item.label}</div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>{item.price}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Credit Log — Audit Trail */}
      {creditLog.length > 0 && (
        <div style={{ maxWidth: 720, margin: "32px auto 0", padding: "0 24px" }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: TXT, marginBottom: 12 }}>
            {zh ? "积分明细" : "Credit History"}
          </h2>
          <div style={{ background: "#fff", borderRadius: 14, border: "1px solid rgba(0,0,0,0.05)", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.03)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 60px 100px 120px", padding: "10px 16px", background: "rgba(0,0,0,0.02)", fontSize: 11, color: TXT3, letterSpacing: 1, textTransform: "uppercase" }}>
              <div>{zh ? "说明" : "Note"}</div>
              <div style={{ textAlign: "center" }}>{zh ? "变动" : "Δ"}</div>
              <div style={{ textAlign: "right" }}>{zh ? "余额" : "Balance"}</div>
              <div style={{ textAlign: "right" }}>{zh ? "日期" : "Date"}</div>
            </div>
            {creditLog.slice(0, 20).map((entry, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 60px 100px 120px", padding: "10px 16px", borderTop: "1px solid rgba(0,0,0,0.04)", fontSize: 13, alignItems: "center" }}>
                <div style={{ color: TXT2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{entry.note || (entry.source === "purchase" ? (zh ? "购买" : "Purchase") : entry.source)}</div>
                <div style={{ textAlign: "center", color: entry.delta > 0 ? "#059669" : "#b91c1c", fontWeight: 600 }}>
                  {entry.delta > 0 ? "+" : ""}{entry.delta}
                </div>
                <div style={{ textAlign: "right", color: TXT }}>{entry.balance_after}</div>
                <div style={{ textAlign: "right", color: TXT3, fontSize: 12 }}>
                  {entry.created_at ? new Date(entry.created_at).toLocaleDateString(zh ? "zh-CN" : undefined) : ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment History */}
      <div style={{ maxWidth: 720, margin: "32px auto 0", padding: "0 24px" }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: TXT, marginBottom: 12 }}>
          {zh ? "支付记录" : "Payment History"}
        </h2>
        {paymentsLoading ? (
          <div style={{ textAlign: "center", padding: 24, color: TXT3, fontSize: 13 }}>{zh ? "加载中…" : "Loading…"}</div>
        ) : payments.length === 0 ? (
          <div style={{ background: "#fff", borderRadius: 14, padding: "32px 20px", border: "1px solid rgba(0,0,0,0.05)", textAlign: "center", color: TXT3, fontSize: 13, boxShadow: "0 1px 4px rgba(0,0,0,0.02)" }}>
            {zh ? "暂无支付记录" : "No payment history yet"}
          </div>
        ) : (
          <div style={{ background: "#fff", borderRadius: 14, border: "1px solid rgba(0,0,0,0.05)", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.03)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 60px", padding: "10px 16px", background: "rgba(0,0,0,0.02)", fontSize: 11, color: TXT3, letterSpacing: 1, textTransform: "uppercase" }}>
              <div>{zh ? "日期" : "Date"}</div>
              <div>{zh ? "说明" : "Description"}</div>
              <div style={{ textAlign: "right" }}>{zh ? "积分" : "Credits"}</div>
            </div>
            {payments.slice(0, 20).map((p, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "120px 1fr 60px", padding: "10px 16px", borderTop: "1px solid rgba(0,0,0,0.04)", fontSize: 13, alignItems: "center" }}>
                  <div style={{ color: TXT3, fontSize: 12 }}>
                    {p.created_at ? new Date(p.created_at).toLocaleDateString(zh ? "zh-CN" : undefined) : ""}
                  </div>
                  <div style={{ color: TXT2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {zh ? "购买问事积分" : "Oracle Credit Purchase"}
                  </div>
                  <div style={{ textAlign: "right", color: "#059669", fontWeight: 600 }}>+{p.delta}</div>
                </div>
            ))}
          </div>
        )}
      </div>

      {/* Refund toast */}
      {refundToast && (
        <div style={{
          position:"fixed", bottom:24, left:"50%", transform:"translateX(-50%)", zIndex:9999,
          background: refundToast.type === "success" ? `linear-gradient(135deg, ${GD}, #B8940A)` : "linear-gradient(135deg, #b91c1c, #991b1b)",
          color:"#fff", padding:"12px 28px", borderRadius:10, fontSize:14, fontWeight:600,
          boxShadow:"0 8px 32px rgba(0,0,0,0.2)", animation:"fadeInDown .3s ease",
        }}>
          {refundToast.msg}
        </div>
      )}
      {/* Footer */}
      <div style={{ textAlign: "center", marginTop: 64, padding: "24px", borderTop: "1px solid rgba(0,0,0,0.04)", fontSize: 12, color: TXT3 }}>
        {zh ? "需要帮助？联系 support@lumarcai.com" : "Need help? support@lumarcai.com"}
      </div>
    </div>
  );
}
